import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '@/shared/api/errors';

import { authApi, sessionQueryKey, twoFactorKeys } from './api';
import type {
  RecoveryCodes,
  SecondFactorVerify,
  TwoFactorConfirm,
  TwoFactorEnabled,
  TwoFactorSetup,
  TwoFactorStatus,
  User,
} from './types';

/**
 * The second factor (contract v10.45, ADR 0039 of the back): the code of an authenticator
 * app after the password, its setup, the recovery codes and the remembered devices.
 */

/** The code after the password. Right, the session opens: it goes into the cache. */
export function useVerifySecondFactor() {
  const queryClient = useQueryClient();
  return useMutation<User, ApiError, SecondFactorVerify>({
    mutationFn: authApi.verifySecondFactor,
    onSuccess: (user) => {
      queryClient.setQueryData(sessionQueryKey, user);
    },
  });
}

/**
 * The QR and the secret to set up the app. A POST, but idempotent on the back (the same
 * secret until it is confirmed), so it is read like a query: once while the screen is up,
 * never on focus, and gone from the cache as soon as nobody shows it. The secret is not
 * something to keep in memory longer than the screen that needs it.
 */
export function useTwoFactorSetup(enabled = true) {
  return useQuery<TwoFactorSetup, ApiError>({
    queryKey: twoFactorKeys.setup,
    queryFn: authApi.startTwoFactorSetup,
    enabled,
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * The first code of the app. Turns the second factor on and answers the recovery codes
 * and the session, which replaces the cached one (from a login it is the session that
 * opens now; from the profile, the same session with `two_factor_enabled`).
 */
export function useConfirmTwoFactor() {
  const queryClient = useQueryClient();
  return useMutation<TwoFactorEnabled, ApiError, TwoFactorConfirm>({
    mutationFn: authApi.confirmTwoFactor,
    // The answer carries the ten recovery codes: gone with the screen that shows them.
    gcTime: 0,
    onSuccess: ({ session }) => {
      queryClient.setQueryData(sessionQueryKey, session);
      queryClient.removeQueries({ queryKey: twoFactorKeys.setup });
      void queryClient.invalidateQueries({ queryKey: twoFactorKeys.status });
    },
  });
}

/** The profile's card: on or off, codes left, remembered devices. */
export function useTwoFactorStatus(enabled = true) {
  return useQuery<TwoFactorStatus, ApiError>({
    queryKey: twoFactorKeys.status,
    queryFn: authApi.twoFactorStatus,
    enabled,
  });
}

/** Ten new recovery codes, with the password. The old ones stop working. */
export function useRenewRecoveryCodes() {
  const queryClient = useQueryClient();
  return useMutation<RecoveryCodes, ApiError, string>({
    mutationFn: authApi.renewRecoveryCodes,
    gcTime: 0,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: twoFactorKeys.status });
    },
  });
}

/** That browser asks for the code again at its next login. */
export function useForgetDevice() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: authApi.forgetDevice,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: twoFactorKeys.status });
    },
  });
}
